import { useState } from 'react'
import classNames from 'classnames'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'

import { accordionsRevealAnimation } from './animations'
import { Section } from '@/types'

type AccordionProps = Partial<{
  title: string
  paragraph: string
}>

type AccordionsProps = Section & {
  collection?: AccordionProps[]
}

const Accordion = ({ title, paragraph }: AccordionProps) => {
  const [active, setActive] = useState(false)

  return (
    <div
      className={classNames(
        'relative',
        'before:absolute before:left-0 before:top-0 before:z-0 before:h-full before:w-full before:rounded-xxs before:shadow-3xl'
      )}
      data-motion-accordion
    >
      <div className="relative z-10 grid items-start rounded-xxs bg-white md:rounded-xxs">
        <button
          className="relative flex items-center justify-between space-x-gutter p-sm pr-[7.8rem] text-left md:pr-[9.8rem]"
          onClick={() => setActive((state) => !state)}
        >
          <Typography variant="h6" component="h6" className="py-xs text-dark">
            {title}
          </Typography>
          <span
            className={classNames(
              'absolute right-sm top-1/2 h-[4.4rem] w-[4.4rem] shrink-0 -translate-y-1/2',
              'transition-all duration-300 ease-in-out-cubic',
              {
                'rotate-0': !active,
                'rotate-[180deg]': active
              }
            )}
          >
            <SVGIcon
              symbol="chevron"
              className={classNames(
                'absolute left-1/2 top-1/2 h-[1.7rem] w-[1.7rem] -translate-x-1/2 -translate-y-[1rem] fill-dark'
              )}
            />
          </span>
        </button>
        <div
          className={classNames(
            'grid overflow-hidden',
            'transition-all duration-300 ease-in-out-cubic',
            {
              'grid-rows-[0fr] opacity-0': !active,
              'grid-rows-[1fr] opacity-100': active
            }
          )}
        >
          <div className="overflow-hidden">
            <Typography
              variant="body"
              className="block px-sm pb-[2.8rem] pt-[.1em] text-dark"
            >
              {paragraph}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  )
}

const Accordions = ({ collection, ...props }: AccordionsProps) => {
  return (
    <Motion animation={accordionsRevealAnimation}>
      {(motionRef) => (
        <section ref={motionRef} {...props}>
          <Container>
            <Grid className="gap-y-xs">
              {collection?.map((accordion, index) => (
                <div
                  key={`accordion-${index}`}
                  className="col-span-2 md:col-span-6 lg:col-span-12"
                >
                  <Accordion {...accordion} />
                </div>
              ))}
            </Grid>
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default Accordions
